import { PropsWithChildren, useContext } from 'react';
import styled from '@emotion/styled';
import { Card, CardContent, Fade, Grid, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectTitle, selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle, selectIsActiveTitleLoading } from '@/redux/selectors/titleV2Selectors';
import { Spinner } from '@/components/_common';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import OPTitleSearch from '@/components/OrderPlacement/OPTitleSearch/OPTitleSearch';
import OPViewingTitleHeader from '@/components/OrderPlacement/OPStep2FileSelection/OPViewingTitleHeader';
import { lighten } from '@mui/material/styles';

type TitleSearchLoadingCardProps = PropsWithChildren<{
  alternateEntityTypesFilter?: string[];
}>;
const TitleSearchLoadingCard = ({ children, alternateEntityTypesFilter }: TitleSearchLoadingCardProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const activeTitle = isTitleV2 ? useAppSelector(selectActiveTitle) : useAppSelector(selectTitle);
  const isLoading = isTitleV2 ? useAppSelector(selectIsActiveTitleLoading) : useAppSelector(selectTitleLoading);
  const isFocus = window.location.pathname.includes('focus');

  return (
    <Fade in timeout={400}>
      <Grid className="title-search-loading-grid" xs={12} md={7} item position="relative">
        <OPTitleSearch alternateEntityTypesFilter={alternateEntityTypesFilter} />
        {!isLoading && <OPViewingTitleHeader />}
        {!activeTitle ? (
          <PlaceholderCard focus={isFocus} elevation={0}>
            <CardContent>
              <Grid container flexDirection="column" alignItems="center" justifyContent="center" minHeight={isFocus ? 500 : 155} my={2}>
                {isLoading ? (
                  <Grid item pt={6}>
                    <Spinner contained />
                    <Typography variant="subtitle2" sx={{ mt: 3, opacity: 0.8 }} align="center">
                      Loading Title
                    </Typography>
                  </Grid>
                ) : (
                  <Grid item sx={{ opacity: isFocus ? 0.05 : 0.1 }}>
                    <PolarisIcon icon="movie_search" width={isFocus ? 160 : 100} height={isFocus ? 145 : 100} />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </PlaceholderCard>
        ) : (
          children
        )}
      </Grid>
    </Fade>
  );
};

export default TitleSearchLoadingCard;

const PlaceholderCard = styled(Card, { shouldForwardProp: (prop) => prop !== 'focus' })<{ focus: boolean }>(({ theme, focus }) => ({
  overflow: 'visible',
  position: 'relative',
  background: focus ? (theme.palette.mode === 'dark' ? '#1A1F2A' : lighten('#1A1F2A', 0.97)) : theme.palette.background.paper
}));
